import { useCallback, useEffect } from 'react';
import { useStore } from '@nanostores/react';
import { Badge, Button, Card, Divider, HStack, Heading, Text, VStack } from '@astryxdesign/core';
import { useNavigate } from 'react-router';

import { formatDate, formatLimit, formatPrice, formatQuota, type Plan } from '../lib/api';
import { $isAuthenticated } from '../stores/auth';
import { $pendingPlan, $subscription, $switching, loadBilling, switchPlan } from '../stores/billing';

interface PlanCardsProps {
  plans: Plan[];
  /** Plan the viewer is currently on, if any. */
  currentCode?: string;
  /** Plan code whose CTA should show a spinner. */
  pendingCode?: string | null;
  /** Plan the subscription already switches to at the period end. */
  scheduledCode?: string;
  onSelect?: (plan: Plan) => void;
  /** When there is no session, CTAs link to signup instead of calling onSelect. */
  ctaHref?: string;
}

export function PlanCards({
  plans,
  currentCode,
  pendingCode,
  scheduledCode,
  onSelect,
  ctaHref,
}: PlanCardsProps) {
  const current = plans.find((p) => p.code === currentCode);

  return (
    <div className="pricing-grid">
      {plans.map((plan) => {
        const isCurrent = plan.code === currentCode;
        const isScheduled = plan.code === scheduledCode;
        // Naming the direction matters: one is charged now, the other waits for
        // the renewal, and the customer should know which before clicking.
        const isDowngrade = current !== undefined && plan.price_cents < current.price_cents;
        return (
          <Card
            key={plan.code}
            padding={3}
            variant="default"
            className={`plan-card lift${plan.highlighted ? ' plan-card--featured' : ''}`}
          >
            <VStack gap={2} height="100%">
              <HStack gap={1} vAlign="center" justify="between" wrap="wrap">
                <Heading level={3}>{plan.name}</Heading>
                {plan.highlighted && !isCurrent && !isScheduled && (
                  <Badge label="Most popular" variant="info" />
                )}
                {isCurrent && <Badge label="Current" variant="success" />}
                {isScheduled && <Badge label="Scheduled" variant="warning" />}
              </HStack>

              <HStack gap={0.5} vAlign="end">
                <span className="plan-price">
                  {plan.contact_sales ? 'Custom' : formatPrice(plan.price_cents, plan.currency)}
                </span>
                {!plan.contact_sales && (
                  <Text size="sm" color="secondary">
                    /{plan.interval}
                  </Text>
                )}
              </HStack>

              <Text size="sm" color="secondary">
                {plan.tagline}
              </Text>

              <Divider variant="subtle" />

              <VStack gap={1}>
                <KeyStat label="Monthly calls" value={formatQuota(plan.monthly_quota)} />
                <KeyStat label="Rate limit" value={`${plan.rate_limit_rpm}/min · burst ${plan.burst_size}`} />
                <KeyStat label="Series / request" value={formatLimit(plan.max_series, plan.contact_sales)} />
                <KeyStat
                  label="Context window"
                  value={`${formatLimit(plan.max_context, plan.contact_sales)} points`}
                />
                <KeyStat label="Max horizon" value={`${plan.max_horizon} steps`} />
                {plan.support_level && <KeyStat label="Support" value={plan.support_level} />}
              </VStack>

              <Divider variant="subtle" />

              <VStack gap={0.5} as="ul">
                {plan.features.map((f) => (
                  <HStack key={f} gap={1} vAlign="start" as="li">
                    <Text size="sm" color="accent">
                      ✓
                    </Text>
                    <Text size="sm" color="secondary">
                      {f}
                    </Text>
                  </HStack>
                ))}
              </VStack>

              <div className="plan-card__spacer" />

              {plan.contact_sales ? (
                <Button label="Talk to sales" variant="secondary" width="100%" href="mailto:team@vectime.cloud" />
              ) : ctaHref ? (
                // Signed-in visitors see the same status wording as the billing
                // page — a plan they already hold must not read "Choose".
                <Button
                  label={
                    currentCode
                      ? ctaLabel(plan, isCurrent, isScheduled, isDowngrade)
                      : plan.price_cents === 0
                        ? 'Start free'
                        : `Choose ${plan.name}`
                  }
                  variant={plan.highlighted && !isCurrent ? 'primary' : 'secondary'}
                  width="100%"
                  isDisabled={isCurrent || isScheduled}
                  href={ctaHref}
                />
              ) : (
                <Button
                  label={ctaLabel(plan, isCurrent, isScheduled, isDowngrade)}
                  variant={plan.highlighted && !isCurrent ? 'primary' : 'secondary'}
                  width="100%"
                  isDisabled={isCurrent || isScheduled}
                  isLoading={pendingCode === plan.code}
                  onClick={() => onSelect?.(plan)}
                />
              )}
            </VStack>
          </Card>
        );
      })}
    </div>
  );
}

/**
 * Plan CTA behaviour for the marketing pages when a session exists: pressing a
 * plan starts the same checkout as /app/billing instead of dropping the visitor
 * on that page to click twice. Only outcomes that produce a notice or an error
 * need the billing page, so those are the ones that navigate.
 *
 * It also reads the billing record, because a scheduled change lives there and
 * not on the session: without it these pages would go on offering a downgrade
 * the customer has already booked.
 */
export function usePlanCheckout() {
  const navigate = useNavigate();
  const isAuthed = useStore($isAuthenticated);
  const pendingCode = useStore($switching);
  const scheduled = useStore($pendingPlan);
  const subscription = useStore($subscription);

  useEffect(() => {
    if (isAuthed) void loadBilling();
  }, [isAuthed]);

  const onSelect = useCallback(
    async (plan: Plan) => {
      const outcome = await switchPlan(plan.code);
      if (outcome !== 'checkout') navigate('/app/billing');
    },
    [navigate],
  );

  return {
    isAuthed,
    onSelect: isAuthed ? onSelect : undefined,
    pendingCode: isAuthed ? pendingCode : null,
    scheduledCode: scheduled?.code,
    // A badge alone says "something is scheduled" without saying when, which is
    // the part that decides whether the customer needs to do anything today.
    scheduledNotice: scheduled
      ? `You move to ${scheduled.name} ${
          subscription ? `on ${formatDate(subscription.period_end)}` : 'at your renewal'
        } — until then you keep your current limits.`
      : null,
  };
}

function ctaLabel(plan: Plan, isCurrent: boolean, isScheduled: boolean, isDowngrade: boolean): string {
  if (isCurrent) return 'Current plan';
  if (isScheduled) return 'Scheduled for renewal';
  if (plan.price_cents === 0) return 'Downgrade to Free';
  return isDowngrade ? `Downgrade to ${plan.name}` : `Upgrade to ${plan.name}`;
}

function KeyStat({ label, value }: { label: string; value: string }) {
  return (
    <HStack gap={1} justify="between" vAlign="end">
      <Text size="sm" color="secondary">
        {label}
      </Text>
      <Text size="sm" weight="medium">
        {value}
      </Text>
    </HStack>
  );
}
